import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { useUserActions } from '../../auth/hooks/useUserActions';
import { AdminModalContentProps } from 'npo/pages/ReportsBeta/ProgramModal/AdminModalContent';
import AdminModal from 'npo/pages/ReportsBeta/ProgramModal/AdminModal';

const getBackgroundColor = (status: FeedbackStatus) => {
  switch (status) {
    case 'Awaiting TI Feedback':
    case 'Awaiting TI Verification':
      return COLORS.ctaBrandBackground;

    case 'Awaiting Funder Feedback':
    case 'Awaiting Funder Verification':
      return COLORS.ctaPrimaryBackground;

    case 'TI Feedback Provided':
    case 'Funder Feedback Provided':
      return COLORS.successBackground;

    case 'Finalized':
    case 'Published':
      return COLORS.success;

    case 'Draft':
    case 'Interim Updates Needed':
    case 'Final Updates Needed':
      return COLORS.grayLight;

    default:
      return 'unset';
  }
};

interface ReportStatusProps {
  status: FeedbackStatus;
  className?: string;
  width?: number;
  allowEdit?: boolean;
  marginTop?: number;
  marginBottom?: number;
  borderRadius?: number;
  padding?: string;
  hideText?: boolean;
  onReportStatusClick?: () => unknown;
}

const StyledDiv = styled.div<{
  status: FeedbackStatus;
  padding?: string;
  width?: number;
  marginTop: number;
  marginBottom: number;
  borderRadius: number;
  onClick?: () => unknown;
}>`
  ${({ onClick }) => (onClick ? 'cursor: pointer;' : '')}
  text-align: center;
  margin-top: ${({ marginTop }) => marginTop}px;
  margin-bottom: ${({ marginBottom }) => marginBottom}px;
  ${({ width }) => (width ? `width: ${width}px;` : '')}
  border-radius: ${({ borderRadius }) => borderRadius}px;
  ${({ padding }) => (padding ? `padding: ${padding};` : '')}
  ${({ status }) =>
    status === 'Published' || status === 'Finalized'
      ? `color: ${COLORS.whiteBase};`
      : ''}
  background-color: ${({ status }) => getBackgroundColor(status)};
`;

const ReportStatus: React.FC<ReportStatusProps> = ({
  marginTop = 12,
  marginBottom = 12,
  borderRadius = 44,
  padding = SPACERS.tiny,
  width,
  status,
  hideText,
  className,
  allowEdit,
  onReportStatusClick,
}) => {
  const { isAdmin } = useUserActions();

  return (
    <StyledDiv
      width={width}
      status={status}
      padding={padding}
      marginTop={marginTop}
      marginBottom={marginBottom}
      borderRadius={borderRadius}
      className={className}
      onClick={allowEdit || isAdmin ? onReportStatusClick : undefined}
    >
      {!hideText && (
        <>
          <strong>Status:</strong>{' '}
        </>
      )}
      {status}
    </StyledDiv>
  );
};

export const ReportStatusModal: React.FC<{
  allowEdit?: boolean;
  dbId: AdminModalContentProps['report']['dbId'];
  stage: AdminModalContentProps['report']['stage'];
  status: AdminModalContentProps['report']['status'];
  reportingType: AdminModalContentProps['report']['reportingType'];
  year: AdminModalContentProps['report']['year'] | undefined;
  feedbackType: AdminModalContentProps['report']['feedbackType'];
  optedInFullReporting: AdminModalContentProps['report']['optedInFullReporting'];
} & Omit<ReportStatusProps, 'onReportStatusClick'>> = ({
  dbId,
  year,
  stage,
  status,
  feedbackType,
  reportingType,
  optedInFullReporting,
  ...rest
}) => {
  const [showStatusModal, setShowStatusModal] = useState<
    AdminModalContentProps['report'] | undefined
  >(undefined);

  const handleStatusClosed = useCallback(() => {
    setShowStatusModal(undefined);
  }, [setShowStatusModal]);

  const onReportStatusClick = useCallback(() => {
    setShowStatusModal({
      dbId,
      stage,
      status,
      year: year || '',
      feedbackType,
      reportingType,
      optedInFullReporting,
    });
  }, [
    dbId,
    stage,
    status,
    year,
    feedbackType,
    optedInFullReporting,
    reportingType,
  ]);

  return (
    <>
      <ReportStatus
        {...rest}
        status={status}
        onReportStatusClick={onReportStatusClick}
      />
      {showStatusModal && (
        <AdminModal
          open={true}
          handleClose={handleStatusClosed}
          report={showStatusModal}
        />
      )}
    </>
  );
};

export default ReportStatus;
